import React, { useState } from 'react';
import { Badge, Button } from 'reactstrap';
import { connect } from 'react-redux';
import SweetAlert from 'react-bootstrap-sweetalert';
import EdittransactionModal from '../pages/Child/EdittransactionModal';
import { deleteTransaction, refetchTransactionStatus, editTransaction } from '../api/transaction';
import { UPDATE_TRANSACTION } from '../pages/Utility/PermissionKeys';
import { validatePermissionKey } from '../helpers/permission';
import { getEffectivePenalty } from '../helpers/transaction';

const BATCH_COLORS = ['#ffffff', '#eef6fb'];

const TransactionTableRow = ({ transaction, childDetail, refresh, authUser, batchGroupIndex = 0 }) => {
  const rowBg = BATCH_COLORS[batchGroupIndex % BATCH_COLORS.length];
  const [editModal, setEditModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMarkUnpaid, setConfirmMarkUnpaid] = useState(false);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ open: false, message: '', type: 'success' });

  const {
    InvoiceID,
    dueAmount,
    totalAmountToPay,
    paidAmount,
    batchId,
    discountAmount,
    discountType,
    quarter,
    isPaid,
    _id,
    dueDate,
    penaltyDeadline
  } = transaction;

  const effectivePenalty = getEffectivePenalty(transaction);

  const quarterLabel = (() => {
    if (quarter == null) return '—';
    const q = batchId?.quarters?.[quarter - 1];
    if (!q?.month) return `Q${quarter}`;
    return new Date(q.month).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' });
  })();

  const amountDue = isPaid
    ? Number(paidAmount || 0).toFixed(2)
    : (Number(totalAmountToPay || 0) - Number(discountAmount || 0) + effectivePenalty).toFixed(2);

  const handleDeleteTxn = async () => {
    setConfirmDelete(false);
    const res = await deleteTransaction(_id);
    if (res?.error) {
      setAlertMessage({ open: true, message: res.error, type: 'error' });
    } else {
      setAlertMessage({ open: true, message: 'Transaction deleted successfully', type: 'success' });
    }
  };

  const handleRefreshStatus = async () => {
    setRefetchLoading(true);
    const response = await refetchTransactionStatus(_id);
    if (response?.error) {
      setAlertMessage({ open: true, message: response.error, type: 'error' });
    } else {
      setAlertMessage({ open: true, message: 'Transaction status refreshed successfully', type: 'success' });
    }
    setRefetchLoading(false);
  };

  const canEdit = authUser?.user_type === 'super_admin' || validatePermissionKey(authUser, UPDATE_TRANSACTION);
  const isSuperAdmin = authUser?.user_type === 'super_admin';

  const handleMarkUnpaid = async () => {
    setConfirmMarkUnpaid(false);
    const res = await editTransaction({ isPaid: false, paidAmount: 0 }, _id);
    if (res?.error) {
      setAlertMessage({ open: true, message: res.error, type: 'error' });
    } else {
      setAlertMessage({ open: true, message: 'Transaction marked as unpaid and payment logs cleared.', type: 'success' });
    }
  };

  return (
    <>
      {alertMessage.open && (
        <SweetAlert
          danger={alertMessage.type === 'error'}
          success={alertMessage.type === 'success'}
          title={alertMessage.type === 'success' ? 'Updated' : 'Error'}
          onConfirm={() => {
            setAlertMessage({ open: false, message: '', type: 'success' });
            refresh();
          }}
        >
          {alertMessage.message}
        </SweetAlert>
      )}
      {confirmMarkUnpaid && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Yes, mark unpaid"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
          title="Mark as Unpaid?"
          onConfirm={handleMarkUnpaid}
          onCancel={() => setConfirmMarkUnpaid(false)}
          focusCancelBtn
        >
          This will mark the transaction as unpaid and clear all payment logs. This action cannot be undone.
        </SweetAlert>
      )}
      {confirmDelete && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Confirm"
          confirmBtnBsStyle="danger"
          title="Are you sure?"
          onConfirm={handleDeleteTxn}
          onCancel={() => setConfirmDelete(false)}
          focusCancelBtn
        >
          This will permanently delete the transaction and this action cannot be undone.
        </SweetAlert>
      )}
      <EdittransactionModal
        modalOpenState={editModal}
        closeModalHandler={() => setEditModal((p) => !p)}
        data={transaction}
        childId={childDetail?._id}
        childinfo={childDetail}
        refresh={refresh}
      />
      <tr style={{ backgroundColor: rowBg }}>
        <td style={{ fontSize: '0.78rem', color: '#888' }}>{InvoiceID || '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{batchId?.name}</td>
        <td style={{ textAlign: 'right' }}>₹{batchId?.totalFee ?? '—'}</td>
        <td style={{ textAlign: 'right' }}>₹{dueAmount}</td>
        <td style={{ fontSize: '0.82rem' }}>{quarterLabel}</td>
        <td style={{ textAlign: 'right' }}>₹{Number(totalAmountToPay).toFixed(2)}</td>
        <td>
          <Badge
            style={{
              backgroundColor: isPaid ? '#28a745' : '#e57373',
              padding: '0.35em 0.65em'
            }}
          >
            {isPaid ? 'Paid' : 'Unpaid'}
          </Badge>
        </td>
        <td style={{ textAlign: 'right', color: discountAmount ? '#c0392b' : undefined }}>
          {discountAmount ? `₹${discountAmount}` : '—'}
          {discountAmount && discountType ? (
            <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>{discountType}</small>
          ) : null}
        </td>
        <td style={{ textAlign: 'right', fontWeight: 500 }}>₹{amountDue}</td>
        <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
          {dueDate
            ? new Date(dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—'}
        </td>
        <td style={{ textAlign: 'right', fontSize: '0.82rem' }}>
          {effectivePenalty > 0
            ? <span style={{ color: '#c0392b' }}>₹{effectivePenalty}</span>
            : <span>₹0{penaltyDeadline && !isPaid
                ? <small className="d-block text-muted" style={{ fontSize: '0.72rem' }}>
                    From {new Date(penaltyDeadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </small>
                : null}
              </span>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {isPaid ? null : (
            <Button
              size="sm"
              color="danger"
              className="mr-1 mb-1"
              onClick={() => setConfirmDelete(true)}
            >
              <i className="ri-delete-bin-line"></i>
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              color="warning"
              className="mr-1 mb-1"
              onClick={() => setEditModal((p) => !p)}
              disabled={isPaid && !transaction?.isCashPayment}
            >
              <i className="fas fa-edit"></i>
            </Button>
          )}
          {!isPaid && (
            <Button
              size="sm"
              color="info"
              className="mr-1 mb-1"
              onClick={handleRefreshStatus}
              disabled={refetchLoading}
            >
              {refetchLoading ? '...' : <i className="fas fa-sync-alt"></i>}
            </Button>
          )}
          {isSuperAdmin && isPaid && (
            <Button
              size="sm"
              color="secondary"
              className="mb-1"
              title="Mark as Unpaid"
              onClick={() => setConfirmMarkUnpaid(true)}
            >
              <i className="fas fa-undo"></i>
            </Button>
          )}
        </td>
      </tr>
    </>
  );
};

const mapStateToProps = (state) => ({ authUser: state.Login?.user });
export default connect(mapStateToProps)(TransactionTableRow);
